import { Option } from 'types/Generics';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { PetType } from 'types/Pet';
import { Room } from 'types/Room';

export interface Service {
    id: number;
    name: string;
    description: string;
    fixedPrice: Array<ServiceFixedPrice>;
    variablePrice?: ServiceVariablePrice;
    petList: Array<string>;
    variableDuration: Array<ServiceVariableDuration> | ServiceMetadataDuration;
    durationHours?: number;
    durationMinutes?: number;
    staffIds: Array<number>;
    businessArea: MarketplaceServiceCategory;

    daycareMetadata?: DaycareMetadata;
    boardingMetadata?: BoardingMetadata;
    evaluationMetadata?: EvaluationMetadata;

    // Daycare-specific fields
    dropOffTime?: { start: number; end: number };
    pickUpTime?: { start: number; end: number };
    // Boarding-specific fields
    checkInTime?: { start: number; end: number };
    checkOutTime?: { start: number; end: number };

    rooms?: Array<Room>;
}

export interface BusinessAreasWithServices {
    id: string;
    hasService: boolean;
    trainingAtFacility?: boolean;
    trainingInHome?: boolean;
    walkingAtFacility?: boolean;
    walkingInHome?: boolean;
}

export interface ServiceFixedPrice {
    petType: PetType;
    price?: number;
}

export interface ServiceVariablePrice {
    variables: ServicePricingVariables;
    prices: Array<ServiceVariablePriceOption>;
}

export interface ServicePricingVariables {
    petTypeVariable: boolean;
    petSizeVariable: boolean;
    hairTypeVariable: boolean;
    hairLengthVariable: boolean;
    exoticTypeVariable: boolean;
}

export interface ServiceVariablePriceOption {
    price?: number;
    petType?: PetType;
    petSize?: Option<string>;
    hairLength?: Option<string>;
}

export interface ServiceVariableDuration {
    petSize: Option<string>;
    duration: ServiceDuration;
}

export interface ServiceDuration {
    hours: number;
    minutes: number;
}

export interface ServiceMetadataDuration {
    metadata: string;
}

export interface DaycareMetadata {
    durationOption: DaycareDurationOption;
    durationValue: string;
    durationHours: number;
    latePickupFee: DaycareLatePickupFee;
    roomsEnabled: boolean;
    selectedRooms: Array<number>;
}

export interface BoardingMetadata {
    dropoff24h: boolean;
    roomsEnabled: boolean;
    selectedRooms: Array<number>;
    minNights: number;
    latePickupFee: BoardingLatePickupFee;
}

export const DAYCARE_DURATION_OPTIONS = {
    HOURLY: 'hourly',
    HALF_DAY: 'half_day',
    FULL_DAY: 'full_day'
} as const;

export const LATE_PICKUP_FEE_TYPES = {
    PER_HOUR: 'per_hour',
    ADDITIONAL_HALF_DAY: 'additional_half_day',
    ADDITIONAL_FULL_DAY: 'additional_full_day'
} as const;

// Boarding-specific constants and types
export const LATE_CHECKOUT_FEE_TYPES = {
    FIXED_FEE: 'fixed_fee',
    PER_HOUR: 'per_hour',
    ADDITIONAL_NIGHT: 'additional_night'
} as const;

export type DaycareDurationOption =
    (typeof DAYCARE_DURATION_OPTIONS)[keyof typeof DAYCARE_DURATION_OPTIONS];
export type LatePickupFeeType = (typeof LATE_PICKUP_FEE_TYPES)[keyof typeof LATE_PICKUP_FEE_TYPES];
export type LateCheckoutFeeType =
    (typeof LATE_CHECKOUT_FEE_TYPES)[keyof typeof LATE_CHECKOUT_FEE_TYPES];

export interface DaycareLatePickupFee {
    enabled: boolean;
    type: LatePickupFeeType;
    gracePeriod: number;
    fees: Record<string, number>; // petTypeId → amount
}

export interface BoardingLatePickupFee {
    enabled: boolean;
    type: LateCheckoutFeeType;
    gracePeriod: number;
    fees: Record<string, number>; // petTypeId → amount
}

export interface EvaluationMetadata {
    businessAreas: Array<MarketplaceServiceCategory>;
    expirationInMonths: number | null;
}
